/**
 * §B-6 user editor — Drawer form (name / email / role / active). For an existing
 * user it also manages the packer PIN via the dedicated set-pin / reset-pin
 * actions (a PIN is a one-way hash, set separately from the profile save). Saves
 * via the ADMIN API with optimistic concurrency; surfaces last-admin / self
 * guards (409) distinctly.
 */
import { useState } from "react";
import { ApiError, api, type AdminUser, type UserInput } from "../../../lib/api.js";
import type { Role } from "@cairn/shared";
import { Drawer, Field, Input, Select, Button, StateChip, useToast } from "../../../components/ui/index.js";

export function UserEditor({ user, onClose, onSaved }: { user: AdminUser | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isNew = user === null;

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [role, setRole] = useState<Role>(user?.role ?? "PACKER");
  const [active, setActive] = useState(user?.active ?? true);
  const [pin, setPin] = useState("");
  const [hasPin, setHasPin] = useState(user?.hasPin ?? false);
  const [saving, setSaving] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);

  function guardToast(e: unknown): boolean {
    if (e instanceof ApiError && e.status === 409) {
      const code = (e.detail as { code?: string } | undefined)?.code;
      const msg =
        code === "LAST_ADMIN" ? "Can’t demote/delete the last active admin."
        : code === "SELF_DEMOTE" ? "You can’t remove your own admin access."
        : code === "SELF_DELETE" ? "You can’t delete your own account."
        : code === "CONFLICT" && isNew ? "A user with that email already exists."
        : "This user changed since you opened it — reloading.";
      toast(msg, "danger");
      if (!isNew && code !== "LAST_ADMIN" && code !== "SELF_DEMOTE" && code !== "CONFLICT") onSaved();
      return true;
    }
    return false;
  }

  async function save() {
    if (!name.trim()) return toast("Name is required.", "danger");
    if (role !== "PACKER" && !email.trim()) return toast("Email is required for admins/managers.", "danger");
    const input: UserInput = { name: name.trim(), email: email.trim() || null, role, active };
    setSaving(true);
    try {
      if (isNew) await api.admin.createUser(input);
      else await api.admin.updateUser(user.id, input, user.updatedAt);
      toast(isNew ? "User created" : "User saved", "success");
      onSaved();
    } catch (e) {
      if (guardToast(e)) return;
      if (e instanceof ApiError && e.status === 400) toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid user.", "danger");
      else toast("Couldn’t save the user.", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function savePin() {
    if (!/^\d{4,8}$/.test(pin)) return toast("PIN must be 4–8 digits.", "danger");
    if (!user) return;
    setPinBusy(true);
    try {
      await api.admin.setUserPin(user.id, pin);
      setPin("");
      setHasPin(true);
      toast("PIN set", "success");
    } catch {
      toast("Couldn’t set the PIN.", "danger");
    } finally {
      setPinBusy(false);
    }
  }

  async function clearPin() {
    if (!user) return;
    setPinBusy(true);
    try {
      await api.admin.resetUserPin(user.id);
      setHasPin(false);
      toast("PIN cleared", "success");
    } catch {
      toast("Couldn’t clear the PIN.", "danger");
    } finally {
      setPinBusy(false);
    }
  }

  return (
    <Drawer
      onClose={onClose}
      ariaLabel={isNew ? "New user" : `Edit ${user.name}`}
      title={isNew ? "Add user" : "Edit user"}
      meta={<span className="muted">Admins/managers sign in with Google; packers use a PIN.</span>}
    >
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Packer" autoFocus />
          </Field>
          <Field label="Role">
            <Select ariaLabel="Role" value={role} onChange={(v) => setRole(v as Role)} options={[{ value: "ADMIN", label: "Admin" }, { value: "MANAGER", label: "Manager" }, { value: "PACKER", label: "Packer" }]} />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label={role === "PACKER" ? "Email (optional)" : "Email"}>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@alpinenation.com" type="email" />
          </Field>
          <Field label="Status">
            <label className="rule-form__toggle">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>{active ? "Active" : "Inactive"}</span>
            </label>
          </Field>
        </div>

        {!isNew && (
          <fieldset className="secret-group">
            <legend>Packer PIN {hasPin ? <StateChip on onLabel="set" offLabel="" /> : <span className="muted">· not set</span>}</legend>
            <div className="rule-form__row2">
              <Field label="New PIN (4–8 digits)">
                <Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" autoComplete="off" />
              </Field>
              <div className="pin-actions">
                <Button variant="secondary" size="sm" onClick={() => void savePin()} disabled={pinBusy || !pin}>Set PIN</Button>
                {hasPin && <Button variant="ghost" size="sm" onClick={() => void clearPin()} disabled={pinBusy}>Reset</Button>}
              </div>
            </div>
          </fieldset>
        )}

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Add user" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
